/**
 * Owner-scoped conversation history client — GET /v1/conversations*
 * (src/march/api/conversation_routes.py).
 *
 * The backend returns ONLY the authenticated clinician's own conversations;
 * there is no parameter that widens visibility. This is what makes chat
 * history per-doctor instead of per-browser.
 */

import { fetchWithAuth } from './fetchWithAuth';
import { getMarchHttpApiBase, getMarchRuntimeBearer, isMarchHttpApiConfigured } from './marchApiEnv';

export interface ServerConversation {
  conversation_id: string;
  patient_id: string | null;
  title: string | null;
  message_count: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface ServerConversationTurn {
  role: 'user' | 'assistant' | string;
  content: string;
  created_at: string | null;
}

function headers(): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  const key = (import.meta.env.VITE_COMMAND_API_KEY ?? '').trim();
  if (key) h['X-API-Key'] = key;
  const bearer = getMarchRuntimeBearer();
  if (bearer) h.Authorization = `Bearer ${bearer}`;
  return h;
}

export function isConversationsApiAvailable(): boolean {
  return isMarchHttpApiConfigured();
}

export async function fetchMyConversations(params: { patientId?: string; limit?: number } = {}): Promise<ServerConversation[]> {
  if (!isConversationsApiAvailable()) return [];
  const qs = new URLSearchParams();
  if (params.patientId?.trim()) qs.set('patient_id', params.patientId.trim());
  if (params.limit) qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  const res = await fetchWithAuth(`${getMarchHttpApiBase()}/v1/conversations${suffix}`, {
    method: 'GET',
    extraHeaders: headers(),
  });
  if (!res.ok) return [];
  const body = (await res.json().catch(() => null)) as { conversations?: ServerConversation[] } | null;
  return body?.conversations ?? [];
}

export async function fetchConversationTurns(conversationId: string): Promise<ServerConversationTurn[]> {
  if (!isConversationsApiAvailable() || !conversationId.trim()) return [];
  const res = await fetchWithAuth(
    `${getMarchHttpApiBase()}/v1/conversations/${encodeURIComponent(conversationId.trim())}/turns`,
    { method: 'GET', extraHeaders: headers() },
  );
  if (!res.ok) return [];
  const body = (await res.json().catch(() => null)) as { turns?: ServerConversationTurn[] } | null;
  return body?.turns ?? [];
}
